"use client";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { ReactNode, forwardRef } from "react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import Link from "next/link";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import { ExternalLink, Menu } from "lucide-react";

type ListItemProps = {
  title: string;
  href: string;
  description: string;
  left?: ReactNode;
  external?: boolean;
};

export const servicesList: ListItemProps[] = [
  {
    title: "PV-Anlagen",
    href: "/info/solar-anlagen-pv-information",
    description:
      "Erfahre mehr über die Möglichkeiten von Photovoltaik für dein Zuhause.",
  },
  {
    title: "Wärmepumpen",
    href: "/info/waermepumpe-information",
    description: "Mit Wärmepumpen kannst du Heizkosten senken. Erfahre mehr.",
  },
  {
    title: "Ladestationen",
    href: "/info/ladestation-elektroauto-plug-in-hybrid-wallbox",
    description:
      "Wir bieten Ladestationen für dein Elektroauto an. Erfahre mehr.",
  },
  {
    title: "Ladestation-Finder",
    href: "/elektroauto-ladestation-finden",
    description: "Finde die nächste Ladestation für dein Elektroauto",
  },
  {
    title: "Preise für Solaranlagen",
    href: "/preise-solaranlagen",
    description: "Preise für Solaranlagen",
  },
];

export const forHomeOwners: ListItemProps[] = [
  {
    title: "Warum vind?",
    description: "Erfahre mehr über uns und unsere Leistungen",
    href: "/fuer-hausbesitzer/warum-vind",
  },
  {
    title: "Solaranlage",
    description: "Strom sparen mit einer Solaranlage",
    href: "/fuer-hausbesitzer/solaranlage",
  },
  {
    title: "Wärmepumpe",
    description: "Heizkosten senken mit einer Wärmepumpe",
    href: "/fuer-hausbesitzer/waermepumpe",
  },
  {
    title: "Ladestation",
    description: "Ladestationen für Elektroautos",
    href: "/fuer-hausbesitzer/ladestation",
  },
  {
    title: "Häufige Fragen",
    description: "Häufig gestellte Fragen",
    href: "/fuer-hausbesitzer/faq",
  },
  {
    title: "Kontakt",
    description: "Nimm Kontakt mit uns auf",
    href: "/kontakt",
  },
  {
    title: "Partner Portal",
    description: "Erfahre mehr über unsere Partnerprogramme",
    href: "/partners/portal",
    left: <ExternalLink size={14} className="text-muted-foreground" />,
    external: true,
  },
];

const forBusinessOwners = [
  {
    title: "Warum vind?",
    description: "Erfahren Sie mehr über uns und unsere Leistungen",
    href: "/fuer-unternehmen/warum-vind",
  },
  {
    title: "Solaranlage",
    description: "Gewinne steigern mit einer Solaranlage",
    href: "/fuer-unternehmen/solaranlage",
  },
  {
    title: "Ladestationen",
    description: "Für Mitarbeiter und Kunden",
    href: "/fuer-unternehmen/ladestation",
  },
  {
    title: "FAQ",
    description: "Häufig gestellte Fragen",
    href: "/fuer-unternehmen/faq",
  },
  {
    title: "Kontakt",
    description: "Kontaktieren Sie uns",
    href: "/kontakt",
  },
];

const forPartners = [
  {
    title: "Vertriebspartner",
    description: "Werden Sie Vertriebspartner",
    href: "/fuer-partner/vertriebspartner",
  },
  {
    title: "Installation DC",
    description: "Werden Sie Partner für die DC-Installation",
    href: "/fuer-partner/installation-dc",
  },
  {
    title: "Installation AC",
    description: "Werden Sie Partner für die AC-Installation",
    href: "/fuer-partner/installation-ac",
  },
  {
    title: "FAQ",
    description: "Häufig gestellte Fragen",
    href: "/fuer-partner/faq",
  },
  {
    title: "Kontakt",
    description: "Kontaktieren Sie uns",
    href: "/kontakt",
  },
];

const catalog: ListItemProps[] = [
  {
    title: "Kategorien",
    href: "/shop",
    description: "Durchstöbere unser Sortiment und finde, was du brauchst.",
  },
  {
    title: "Alle Produkte",
    href: "/shop/alle-produkte",
    description:
      "Sieh dir alle unsere Produkte an einem Ort an. Finde schnell, was du brauchst.",
  },
];

const navList = [
  {
    title: "Für Hausbesitzer",
    children: forHomeOwners,
    href: "/fuer-hausbesitzer/solaranlage",
  },
  {
    title: "Shop",
    children: catalog,
    href: "/shop",
  },
  // {
  //   title: "Für Unternehmen",
  //   children: forBusinessOwners,
  // },
  // {
  //   title: "Für Partner",
  //   children: forPartners,
  // },
];

export const PageNavigation = ({
  navmenuItems = [],
  showProducts = false,
  cart,
}: {
  children?: ReactNode;
  navmenuItems?: { title: string; href: string; description: string }[];
  showProducts?: boolean;
  cart?: ReactNode;
}) => {
  const allNavmenuItems = catalog.concat(navmenuItems);

  return (
    <div className="sticky top-0 z-90 h-16 w-full list-none bg-white px-2 md:px-0">
      <div className="container mx-auto flex h-full flex-row justify-between">
        <Link href="/" className="py-2">
          <div className="hidden md:block">
            <img src="/LogoBlue.svg" width="150" alt="Vind Logo" />
          </div>
          <div className="relative top-0.5 -left-2 w-[40px] md:hidden">
            <img
              src="/LogoOrange_Small.svg"
              className="h-full w-full"
              alt="Vind Logo"
            />
          </div>
        </Link>
        <NavigationMenu>
          <NavigationMenuItem className="hidden md:block">
            <NavigationMenuTrigger>Lösungen</NavigationMenuTrigger>
            <NavigationMenuContent>
              <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                {forHomeOwners.map((service, idx) => (
                  <ListItem
                    key={service.title}
                    title={service.title}
                    href={service.href}
                  >
                    {service.description}
                  </ListItem>
                ))}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
          {showProducts && (
            <NavigationMenuItem className="hidden md:block">
              <NavigationMenuTrigger>Produkte</NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                  {allNavmenuItems.map((item) => (
                    <ListItem
                      key={item.title}
                      title={item.title}
                      href={item.href}
                    >
                      {item.description}
                    </ListItem>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          )}
          <NavigationMenuItem className="hidden md:block">
            <Link href="/ueber-uns" legacyBehavior passHref>
              <NavigationMenuLink
                className={cn(
                  "group bg-background hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-active:bg-accent/50 data-[state=open]:bg-accent/50 inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-hidden disabled:pointer-events-none disabled:opacity-50",
                )}
              >
                Über Uns
              </NavigationMenuLink>
            </Link>
          </NavigationMenuItem>
          <NavigationMenuItem className="md:hidden">
            <Drawer modal snapPoints={[1]}>
              <DrawerTrigger>
                <div
                  className={cn(
                    navigationMenuTriggerStyle(),
                    "w-full justify-start px-8 py-8 text-left",
                  )}
                >
                  <Menu size={12} />
                  <span className="ml-2 text-sm">Menü</span>
                </div>
              </DrawerTrigger>
              <DrawerContent className="mx-auto w-full max-w-md">
                <DrawerHeader>
                  <DrawerTitle>Menü</DrawerTitle>
                </DrawerHeader>
                <div className="mt-auto flex max-h-[80vh] w-full flex-col gap-0 overflow-auto p-4 text-left">
                  {navList.map((link) => (
                    <div className="w-full" key={link.title}>
                      {link.children ? (
                        <Accordion
                          type="single"
                          collapsible
                          defaultValue="item-1"
                        >
                          <AccordionItem value="item-1">
                            <AccordionTrigger className="py-0">
                              <div
                                className={cn(
                                  navigationMenuTriggerStyle(),
                                  "w-full justify-start px-8 py-8 text-left",
                                )}
                              >
                                <span className="ml-2 text-sm">
                                  {link.title}
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              {link.children.map((child) => (
                                <NavigationMenuLink
                                  key={child.href}
                                  href={child.href}
                                  className={cn(
                                    navigationMenuTriggerStyle(),
                                    "flex h-auto w-full flex-col justify-start px-8 py-2 text-left",
                                  )}
                                >
                                  <span className="ml-2 self-start text-sm">
                                    {child.title}
                                  </span>
                                  <span className="ml-2 self-start text-xs text-gray-400">
                                    {child.description}
                                  </span>
                                </NavigationMenuLink>
                              ))}
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      ) : (
                        <NavigationMenuLink
                          href={link.href}
                          className={cn(
                            navigationMenuTriggerStyle(),
                            "w-full justify-start border-b px-8 py-8 text-left",
                          )}
                        >
                          <span className="ml-2 text-sm">{link.title}</span>
                        </NavigationMenuLink>
                      )}
                    </div>
                  ))}
                </div>
                <DrawerFooter className="h-6 w-full"></DrawerFooter>
              </DrawerContent>
            </Drawer>
          </NavigationMenuItem>
        </NavigationMenu>
        <Link href="/kontakt" className="mx-2 hidden self-center md:block">
          <Button className="self-center">Kontakt</Button>
        </Link>
      </div>
    </div>
  );
};

const ListItem = forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a">
>(({ className, title, children, href, ...props }, ref) => {
  return (
    <div>
      <NavigationMenuLink asChild>
        <Link
          ref={ref}
          className={cn(
            "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block space-y-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors select-none",
            className,
          )}
          href={href!}
          {...(props as any)}
        >
          <div className="text-sm leading-none font-medium">{title}</div>
          <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </div>
  );
});

ListItem.displayName = "ListItem";

export const ShopNavigation = ({
  navmenuItems = [],
  showProducts = false,
  cart,
  user,
}: {
  children?: ReactNode;
  navmenuItems?: { title: string; href: string; description: string }[];
  showProducts?: boolean;
  cart?: ReactNode;
  user?: ReactNode;
}) => {
  const allNavmenuItems = catalog.concat(navmenuItems);
  return (
    <div className="sticky top-0 z-90 h-16 w-full list-none bg-white px-2 md:px-0">
      <div className="container mx-auto flex h-full flex-row justify-between">
        <Link href="/" className="py-2">
          <div className="hidden md:block">
            <img src="/LogoBlue.svg" width="150" alt="Vind Logo" />
          </div>
          <div className="relative top-0.5 -left-2 w-[40px] md:hidden">
            <img
              src="/LogoOrange_Small.svg"
              className="h-full w-full"
              alt="Vind Logo"
            />
          </div>
        </Link>
        <NavigationMenu delayDuration={1000}>
          {showProducts && (
            <NavigationMenuItem className="hidden md:block">
              <NavigationMenuTrigger>Entdecken</NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                  {allNavmenuItems.map((item) => (
                    <ListItem
                      key={item.title}
                      title={item.title}
                      href={item.href}
                    >
                      {item.description}
                    </ListItem>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          )}
          <NavigationMenuItem className="hidden md:block">
            <Link href="/ueber-uns" legacyBehavior passHref>
              <NavigationMenuLink
                className={cn(
                  "group bg-background hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-active:bg-accent/50 data-[state=open]:bg-accent/50 inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-hidden disabled:pointer-events-none disabled:opacity-50",
                )}
              >
                Über Uns
              </NavigationMenuLink>
            </Link>
          </NavigationMenuItem>
          <NavigationMenuItem className="md:hidden">
            <Drawer modal snapPoints={[1]}>
              <DrawerTrigger>
                <div
                  className={cn(
                    navigationMenuTriggerStyle(),
                    "w-full justify-start px-8 py-8 text-left",
                  )}
                >
                  <Menu size={12} />
                  <span className="ml-2 text-sm">Menü</span>
                </div>
              </DrawerTrigger>
              <DrawerContent className="mx-auto w-full max-w-md">
                <DrawerHeader>
                  <DrawerTitle>Menü</DrawerTitle>
                </DrawerHeader>
                <div className="mt-auto flex max-h-[80vh] w-full flex-col gap-0 overflow-auto p-4 text-left">
                  {navList.map((link) => (
                    <div className="w-full" key={link.title}>
                      {link.children ? (
                        <Accordion
                          type="single"
                          collapsible
                          defaultValue="item-1"
                        >
                          <AccordionItem value="item-1">
                            <AccordionTrigger className="py-0">
                              <div
                                className={cn(
                                  navigationMenuTriggerStyle(),
                                  "w-full justify-start px-8 py-8 text-left",
                                )}
                              >
                                <span className="ml-2 text-sm">
                                  {link.title}
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              {link.children.map((child) => (
                                <NavigationMenuLink
                                  key={child.href}
                                  href={child.href}
                                  className={cn(
                                    navigationMenuTriggerStyle(),
                                    "flex h-auto w-full flex-col justify-start px-8 py-2 text-left",
                                  )}
                                >
                                  <span className="ml-2 self-start text-sm">
                                    {child.title}
                                  </span>
                                  <span className="ml-2 self-start text-xs text-gray-400">
                                    {child.description}
                                  </span>
                                </NavigationMenuLink>
                              ))}
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      ) : (
                        <NavigationMenuLink
                          href={link.href}
                          className={cn(
                            navigationMenuTriggerStyle(),
                            "w-full justify-start border-b px-8 py-8 text-left",
                          )}
                        >
                          <span className="ml-2 text-sm">{link.title}</span>
                        </NavigationMenuLink>
                      )}
                    </div>
                  ))}
                </div>
                <DrawerFooter className="h-6 w-full"></DrawerFooter>
              </DrawerContent>
            </Drawer>
          </NavigationMenuItem>
        </NavigationMenu>
        <div className="flex flex-row items-center gap-2">
          {user}
          {cart}
        </div>
      </div>
    </div>
  );
};
